/**
 * Configuration for the AI orb WebGL renderer. Mirrors the `config` object
 * accepted by `createOrb()` in `playground/orb-embed.js`.
 */
export interface AiOrbConfig {
  /** Palette hex stops, 2-4 colors. */
  colors: string[]
  /** Seconds per seamless loop. */
  duration: number
  /** Blob size (inverted into noise frequency internally). */
  blobScale: number
  /** How far the flow field carries the noise field per loop. */
  flow: number
  /** Strength of the second, nested warp. */
  turbulence: number
  /** Palette lookup shift. */
  shift: number
  /** Colour dominance exponent (0-1). */
  balance: number
  /** Hue ripple amount. */
  chroma: number
  /** Lightness contrast around mid. */
  contrast: number
  /** Fresnel rim strength. */
  rim: number
  /** Specular highlight strength. */
  specular: number
  /** Internal scatter (fake subsurface) strength. */
  inner: number
  /** Silhouette wobble amount. */
  wobble: number
  /** Fresnel-driven hue rotation (iridescence). */
  iridescence: number
  /** Diffuse shading mix amount. */
  shading: number
  /** Refraction offset applied to the noise sample. */
  refraction: number
  /** Light direction X. */
  lightX: number
  /** Light direction Y. */
  lightY: number
  /** Rim chromatic aberration amount. */
  aberration: number
  /** Grain + dither amount. */
  grain: number
  /** Edge softness. */
  softness: number
  /** Outer glow amount. */
  glow: number
}

export const AI_ORB_DEFAULTS: AiOrbConfig = {
  colors: ['#e6b340', '#eee3c8'],
  duration: 6.3,
  blobScale: 2.45,
  flow: 0,
  turbulence: 0,
  shift: 0.19,
  balance: 0.45,
  chroma: 0.94,
  contrast: 1,
  rim: 1.05,
  specular: 0,
  inner: 0.34,
  wobble: 0.45,
  iridescence: 0,
  shading: 0,
  refraction: 0.56,
  lightX: -0.6,
  lightY: -1,
  aberration: 0.8,
  grain: 0.6,
  softness: 0.015,
  glow: 0.6,
}

export type OrbState = 'idle' | 'working' | 'ready'

/**
 * Shallow-merges a partial config on top of a base config. `colors`, when
 * provided, replaces the base array wholesale rather than merging entries.
 */
export function mergeOrbConfig(base: AiOrbConfig, partial: Partial<AiOrbConfig>): AiOrbConfig {
  return { ...base, ...partial }
}

/**
 * Built-in per-state defaults, each a complete, independent `AiOrbConfig`.
 * This is the code fallback for clients that cannot load the platform-wide
 * config. Keep it synchronized with the production `ai_orb_config` row.
 */
export const AI_ORB_STATE_DEFAULTS: Record<OrbState, AiOrbConfig> = {
  idle: AI_ORB_DEFAULTS,
  working: {
    colors: ['#e6b340', '#ffba1a'],
    duration: 3.9,
    blobScale: 3,
    flow: 0.51,
    turbulence: 1,
    shift: 0.23,
    balance: 0.5,
    chroma: 1.1,
    contrast: 1,
    rim: 1.56,
    specular: 0,
    inner: 0.38,
    wobble: 1,
    iridescence: 0,
    shading: 0.03,
    refraction: 0.6,
    lightX: -0.6,
    lightY: -1,
    aberration: 0.8,
    grain: 0.6,
    softness: 0.015,
    glow: 0.79,
  },
  ready: {
    colors: ['#e6b340', '#9fe916'],
    duration: 8,
    blobScale: 3,
    flow: 0.55,
    turbulence: 0,
    shift: 0.24,
    balance: 0.56,
    chroma: 0.78,
    contrast: 1,
    rim: 1.96,
    specular: 0,
    inner: 0.38,
    wobble: 0.3,
    iridescence: 0,
    shading: 0.03,
    refraction: 0.6,
    lightX: -0.6,
    lightY: -1,
    aberration: 0.8,
    grain: 0.6,
    softness: 0.045,
    glow: 0.76,
  },
}
